import React from "react";
import {
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import moment from "moment";
import CourierRegular from "../../fonts/CourierPrime-Regular.ttf";
import { decrypt } from "../../utils";

Font.register({
  family: "Courier Prime",
  fonts: [{ src: CourierRegular }],
});

const styles = StyleSheet.create({
  page: {
    backgroundColor: "#fbfaf8",
    paddingTop: 28,
    paddingBottom: 18,
    paddingHorizontal: 28,
    fontSize: 10,
    color: "#1f2937",
    fontFamily: "Helvetica",
  },
  shell: {},
  topCard: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    border: "1 solid #e9e3da",
    padding: 14,
    marginBottom: 10,
  },
  topHeader: {
    flexDirection: "row",
    alignItems: "center",
    paddingBottom: 12,
    borderBottom: "1 solid #efe9df",
  },
  avatar: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: "#1f2937",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  avatarImage: {
    width: 42,
    height: 42,
    borderRadius: 21,
    marginRight: 12,
    objectFit: "cover",
  },
  avatarText: {
    color: "#ffffff",
    fontSize: 14,
    fontWeight: 700,
  },
  titleBlock: {
    flex: 1,
  },
  titleTopRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 5,
  },
  clientName: {
    fontSize: 20,
    fontWeight: 700,
    color: "#1f2937",
  },
  confidentialText: {
    fontSize: 8,
    color: "#c0b6a8",
    textAlign: "right",
    maxWidth: 210,
  },
  summaryRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
  },
  summaryItem: {
    fontSize: 8,
    color: "#7b8190",
    marginRight: 12,
    marginBottom: 2,
  },
  summaryAlert: {
    fontSize: 8,
    color: "#d04b3f",
    fontWeight: 700,
    marginBottom: 2,
  },
  metricRow: {
    flexDirection: "row",
    paddingTop: 12,
  },
  metricCell: {
    flex: 1,
    paddingHorizontal: 10,
    borderRight: "1 solid #efe9df",
  },
  metricCellFirst: {
    paddingLeft: 0,
  },
  metricCellLast: {
    borderRight: "0 solid transparent",
    paddingRight: 0,
  },
  metricLabel: {
    fontSize: 7,
    textTransform: "uppercase",
    color: "#9b9fa8",
    marginBottom: 4,
    fontWeight: 700,
  },
  metricValue: {
    fontSize: 13,
    color: "#2d3138",
    fontWeight: 700,
  },
  contentRow: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  leftCol: {
    width: "46%",
    paddingRight: 7,
  },
  rightCol: {
    width: "54%",
    paddingLeft: 7,
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: 10,
    border: "1 solid #e9e3da",
    overflow: "hidden",
    marginBottom: 10,
  },
  cardHeader: {
    backgroundColor: "#f3eee6",
    paddingHorizontal: 12,
    paddingVertical: 8,
    flexDirection: "row",
    alignItems: "center",
  },
  cardHeaderGrow: {
    flex: 1,
  },
  headerIcon: {
    width: 14,
    height: 14,
    borderRadius: 7,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 7,
  },
  serviceIconDoc: {
    width: 8,
    height: 10,
    border: "1.1 solid #4d8f2d",
    borderRadius: 1.5,
    backgroundColor: "#f7fbf1",
    paddingTop: 1.5,
    paddingHorizontal: 1.2,
    justifyContent: "space-between",
  },
  serviceIconLine: {
    height: 1,
    borderRadius: 1,
    backgroundColor: "#4d8f2d",
  },
  medIconBottle: {
    width: 8,
    height: 8.5,
    border: "1.1 solid #b67a12",
    borderRadius: 1.6,
    backgroundColor: "#fffaf0",
    position: "relative",
  },
  medIconCap: {
    position: "absolute",
    top: -2.2,
    left: 1.5,
    width: 5,
    height: 2.4,
    border: "1.1 solid #b67a12",
    borderBottom: "0 solid transparent",
    borderTopLeftRadius: 1,
    borderTopRightRadius: 1,
    backgroundColor: "#fffaf0",
  },
  headerIconText: {
    fontSize: 8,
    fontWeight: 700,
    color: "#ffffff",
  },
  cardHeaderText: {
    fontSize: 10,
    fontWeight: 700,
    color: "#383d45",
    textTransform: "uppercase",
  },
  cardBody: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  supportItem: {
    flexDirection: "row",
    borderBottom: "1 solid #f2ede5",
    paddingVertical: 9,
  },
  supportItemLast: {
    borderBottom: "0 solid transparent",
  },
  supportBadge: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
    marginTop: 1,
  },
  supportBadgeText: {
    fontSize: 8,
    fontWeight: 700,
  },
  supportMeta: {
    flex: 1,
  },
  supportRole: {
    fontSize: 7,
    color: "#9b9fa8",
    marginBottom: 2,
    textTransform: "capitalize",
  },
  supportName: {
    fontSize: 11,
    color: "#2b3138",
    fontWeight: 700,
    marginBottom: 2,
  },
  supportContact: {
    fontSize: 8,
    color: "#8d939f",
  },
  summaryBox: {
    backgroundColor: "#f6f1ea",
    borderRadius: 7,
    padding: 10,
    marginBottom: 9,
  },
  summaryBoxText: {
    fontSize: 9,
    lineHeight: 1.45,
    color: "#626976",
  },
  subSectionTitle: {
    fontSize: 8,
    color: "#9a9ea8",
    textTransform: "uppercase",
    fontWeight: 700,
    marginBottom: 5,
  },
  chipRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 7,
  },
  chip: {
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 3,
    marginRight: 6,
    marginBottom: 6,
  },
  chipText: {
    fontSize: 8,
    fontWeight: 700,
  },
  listItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 8,
  },
  listDot: {
    width: 5,
    height: 5,
    borderRadius: 2.5,
    backgroundColor: "#e2b33f",
    marginTop: 5,
    marginRight: 8,
  },
  listText: {
    flex: 1,
    fontSize: 9,
    color: "#535a66",
    lineHeight: 1.35,
  },
  medsHeaderRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  activeBadge: {
    borderRadius: 10,
    backgroundColor: "#fde6c2",
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  activeBadgeText: {
    fontSize: 8,
    color: "#9b6a0b",
    fontWeight: 700,
  },
  medGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  medCell: {
    width: "50%",
    paddingRight: 10,
    paddingBottom: 8,
    marginBottom: 2,
  },
  medTitleRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  medDot: {
    width: 5,
    height: 5,
    borderRadius: 2.5,
    backgroundColor: "#f0b52f",
    marginRight: 6,
    marginTop: 4,
  },
  medName: {
    flex: 1,
    fontSize: 10,
    fontWeight: 700,
    color: "#2e333b",
  },
  medSchedule: {
    borderRadius: 10,
    backgroundColor: "#ece6ff",
    color: "#7869cc",
    fontSize: 7,
    fontWeight: 700,
    paddingHorizontal: 7,
    paddingVertical: 2,
  },
  medMeta: {
    fontSize: 8,
    color: "#8a909d",
    marginLeft: 11,
    lineHeight: 1.35,
  },
  footer: {
    position: "absolute",
    flexDirection: "row",
    justifyContent: "space-between",
    left: 28,
    right: 28,
    bottom: 12,
  },
  footerText: {
    fontSize: 6.5,
    color: "#c0b6a8",
  },
});

const formatDate = (value) =>
  value ? moment(value).format("MM/DD/YYYY") : "—";

const formatContact = (phone, email) => {
  const parts = [phone, email].filter(Boolean);
  return parts.length ? parts.join("  ·  ") : "—";
};

const getInitials = (value = "") =>
  value
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "CL";

const supportColors = [
  { bg: "#ece8ff", fg: "#6a63d9" },
  { bg: "#dff5ec", fg: "#41a87c" },
  { bg: "#e6f0ff", fg: "#5f84db" },
  { bg: "#f5efe2", fg: "#b18b52" },
  { bg: "#fde9e4", fg: "#cf735f" },
];

const diagnosisList = (clientData) => {
  if (Array.isArray(clientData?.form1?.diagnosis) && clientData.form1.diagnosis.length) {
    return clientData.form1.diagnosis.map((item) =>
      typeof item === "string" ? item : item?.name
    ).filter(Boolean);
  }

  if (Array.isArray(clientData?.form1?.knownIllness) && clientData.form1.knownIllness.length) {
    return clientData.form1.knownIllness.map((item) =>
      typeof item === "string"
        ? item
        : item?.illness || item?.contagious || item?.name
    ).filter(Boolean);
  }

  return [];
};

const supportPeople = (clientData) => {
  const base = [
    {
      role: "Guardian",
      name: clientData?.form2?.emergencyContactName,
      phone: clientData?.form2?.emergencyContactPhoneNumber,
      email: clientData?.form2?.emergencyContactEmail,
    },
    {
      role: "Case manager",
      name: clientData?.form2?.caseManagerName,
      phone: clientData?.form2?.caseManagerPhoneNumber,
      email: clientData?.form2?.caseManagerEmail,
    },
    {
      role: "Primary care physician",
      name: clientData?.form2?.pcpName,
      phone: clientData?.form2?.pcpPhoneNumber,
      email: clientData?.form2?.pcpEmail,
    },
    {
      role: "Pharmacy",
      name: clientData?.form2?.pharmacyName,
      phone: clientData?.form2?.pharmacyPhoneNumber,
      email: clientData?.form2?.pharmacyEmail,
    },
  ].filter((item) => item.name || item.phone || item.email);

  const additional =
    clientData?.form2?.additionalProvider?.map((item) => ({
      role: item?.providerType || "Provider",
      name: item?.providerName,
      phone: item?.providerPhone,
      email: item?.providerEmail,
    })) || [];

  return [...base, ...additional].slice(0, 5);
};

const activeMedications = (clientData) =>
  (clientData?.form3 || [])
    .filter(
      (medicine) =>
        medicine &&
        (medicine?.discontinued === false || !("discontinued" in medicine))
    )
    .slice(0, 5);

const serviceLines = (clientData) =>
  (clientData?.form1?.services || [])
    .map((item) => item?.servicetitle || item?.title || item)
    .filter(Boolean)
    .slice(0, 4);

const contactRow = ({ role, name, phone, email }, index, total) => {
  const palette = supportColors[index % supportColors.length];

  return (
    <View
      key={`${role}-${name}-${index}`}
      style={[
        styles.supportItem,
        index === total - 1 ? styles.supportItemLast : null,
      ]}
    >
      <View style={[styles.supportBadge, { backgroundColor: palette.bg }]}>
        <Text style={[styles.supportBadgeText, { color: palette.fg }]}>
          {getInitials(name || role)}
        </Text>
      </View>
      <View style={styles.supportMeta}>
        <Text style={styles.supportRole}>{role}</Text>
        <Text style={styles.supportName}>{name || "—"}</Text>
        <Text style={styles.supportContact}>{formatContact(phone, email)}</Text>
      </View>
    </View>
  );
};

const ClientSummaryPDF = ({
  clientData,
  facilityName,
  facilityPhoto,
}) => {
  const meds = activeMedications(clientData);
  const support = supportPeople(clientData);
  const diagnoses = diagnosisList(clientData);
  const services = serviceLines(clientData);
  const allergies = (clientData?.form1?.knownAllergies || []).filter(Boolean);
  const dietaries = (clientData?.form1?.dietaries || []).filter(Boolean);
  const rawImageSrc = clientData?.imageurl ? decrypt(clientData?.imageurl) : null;
  const imageSrc = rawImageSrc
    ? `${rawImageSrc}${rawImageSrc.includes("?") ? "&" : "?"}pdf=1`
    : null;
  const clientInitials = getInitials(clientData?.name);
  const summaryLine = [
    clientData?.form1?.gender || "—",
    `DOB: ${formatDate(clientData?.form1?.dob)}`,
    `Admitted: ${formatDate(clientData?.form1?.admissionDate)}`,
    clientData?.form1?.previousAddress,
    clientData?.form1?.levelofcare,
  ].filter(Boolean);

  const resolvedCompanyName =
    clientData?.business?.companyName ||
    clientData?.companyName ||
    facilityName ||
    "";

  return (
    <Document>
      <Page size="LETTER" style={styles.page} wrap={false}>
        <View style={styles.shell}>
          <View>
            <View style={styles.topCard}>
              <View style={styles.topHeader}>
                {imageSrc ? (
                  <Image src={imageSrc} style={styles.avatarImage} />
                ) : (
                  <View style={styles.avatar}>
                    <Text style={styles.avatarText}>{clientInitials}</Text>
                  </View>
              )}
              <View style={styles.titleBlock}>
                <View style={styles.titleTopRow}>
                  <Text style={styles.clientName}>{clientData?.name || "Client"}</Text>
                  <Text style={styles.confidentialText}>
                    {`${resolvedCompanyName || "Business"} · Client Summary — Confidential`}
                  </Text>
                </View>
                <View style={styles.summaryRow}>
                  {summaryLine.map((item, index) => (
                    <Text key={`${item}-${index}`} style={styles.summaryItem}>
                        {item}
                      </Text>
                    ))}
                    <Text style={styles.summaryAlert}>
                      Allergies: {allergies.length ? allergies.join(", ") : "None"}
                    </Text>
                  </View>
                </View>
              </View>

              <View style={styles.metricRow}>
                <View style={[styles.metricCell, styles.metricCellFirst]}>
                  <Text style={styles.metricLabel}>Height</Text>
                  <Text style={styles.metricValue}>
                    {clientData?.form1?.height || "—"}
                  </Text>
                </View>
                <View style={styles.metricCell}>
                  <Text style={styles.metricLabel}>Weight</Text>
                  <Text style={styles.metricValue}>
                    {clientData?.form1?.weight
                      ? `${clientData.form1.weight} lbs`
                      : "—"}
                  </Text>
                </View>
                <View style={styles.metricCell}>
                  <Text style={styles.metricLabel}>Active Medications</Text>
                  <Text style={styles.metricValue}>{meds.length}</Text>
                </View>
                <View style={styles.metricCell}>
                  <Text style={styles.metricLabel}>Level of Care</Text>
                  <Text style={styles.metricValue}>
                    {clientData?.form1?.levelofcare || "—"}
                  </Text>
                </View>
                <View style={[styles.metricCell, styles.metricCellLast]}>
                  <Text style={styles.metricLabel}>Facility</Text>
                  <Text style={styles.metricValue}>{resolvedCompanyName || "—"}</Text>
                </View>
              </View>
            </View>

            <View style={styles.contentRow}>
              <View style={styles.leftCol}>
                <View style={styles.card}>
                  <View style={styles.cardHeader}>
                    <View style={[styles.headerIcon, { backgroundColor: "#dce7ff" }]}>
                      <Text style={[styles.headerIconText, { color: "#5c7bd8" }]}>
                        A
                      </Text>
                    </View>
                    <Text style={styles.cardHeaderText}>Care & Support</Text>
                  </View>
                  <View style={styles.cardBody}>
                    {support.length ? (
                      support.map((item, index) =>
                        contactRow(item, index, support.length)
                      )
                    ) : (
                      <Text style={styles.listText}>No care contacts added</Text>
                    )}
                  </View>
                </View>

                <View style={styles.card}>
                  <View style={styles.cardHeader}>
                  <View style={[styles.headerIcon, { backgroundColor: "#e5f2d5" }]}>
                    <View style={styles.serviceIconDoc}>
                      <View style={styles.serviceIconLine} />
                      <View style={styles.serviceIconLine} />
                      <View style={styles.serviceIconLine} />
                    </View>
                  </View>
                    <Text style={styles.cardHeaderText}>Provider Services</Text>
                  </View>
                  <View style={styles.cardBody}>
                    {services.length ? (
                      services.map((item, index) => (
                        <View key={`${item}-${index}`} style={styles.listItem}>
                          <View style={styles.listDot} />
                          <Text style={styles.listText}>{item}</Text>
                        </View>
                      ))
                    ) : (
                      <Text style={styles.listText}>No provider services listed</Text>
                    )}
                  </View>
                </View>
              </View>

              <View style={styles.rightCol}>
                <View style={styles.card}>
                  <View style={styles.cardHeader}>
                    <View style={[styles.headerIcon, { backgroundColor: "#ffe4e0" }]}>
                      <Text style={[styles.headerIconText, { color: "#d45d52" }]}>
                        D
                      </Text>
                    </View>
                    <Text style={styles.cardHeaderText}>Health & Diagnosis</Text>
                  </View>
                  <View style={styles.cardBody}>
                    <View style={styles.summaryBox}>
                      <Text style={styles.summaryBoxText}>
                        {clientData?.form1?.summary ||
                          "No diagnosis summary available."}
                      </Text>
                    </View>

                    <Text style={styles.subSectionTitle}>Diagnoses</Text>
                    <View style={styles.chipRow}>
                      {diagnoses.length ? (
                        diagnoses.slice(0, 4).map((item, index) => (
                          <View
                            key={`${item}-${index}`}
                            style={[styles.chip, { backgroundColor: "#ffe7e4" }]}
                          >
                            <Text style={[styles.chipText, { color: "#d16a61" }]}>
                              {item}
                            </Text>
                          </View>
                        ))
                      ) : (
                        <Text style={styles.listText}>No diagnoses listed</Text>
                      )}
                    </View>

                    <View style={{ flexDirection: "row" }}>
                      <View style={{ width: "50%", paddingRight: 6 }}>
                        <Text style={styles.subSectionTitle}>Allergies</Text>
                        <View style={styles.chipRow}>
                          {allergies.length ? (
                            allergies.slice(0, 4).map((item, index) => (
                              <View
                                key={`${item}-${index}`}
                                style={[styles.chip, { backgroundColor: "#fdeccf" }]}
                              >
                                <Text style={[styles.chipText, { color: "#b5842a" }]}>
                                  {item}
                                </Text>
                              </View>
                            ))
                          ) : (
                            <Text style={styles.listText}>None</Text>
                          )}
                        </View>
                      </View>

                      <View style={{ width: "50%", paddingLeft: 6 }}>
                        <Text style={styles.subSectionTitle}>Dietary Restrictions</Text>
                        <View style={styles.chipRow}>
                          {dietaries.length ? (
                            dietaries.slice(0, 4).map((item, index) => (
                              <View
                                key={`${item}-${index}`}
                                style={[
                                  styles.chip,
                                  { backgroundColor: index % 2 ? "#dff3ef" : "#eef7d9" },
                                ]}
                              >
                                <Text
                                  style={[
                                    styles.chipText,
                                    { color: index % 2 ? "#4f9f96" : "#6caa54" },
                                  ]}
                                >
                                  {item}
                                </Text>
                              </View>
                            ))
                          ) : (
                            <Text style={styles.listText}>None</Text>
                          )}
                        </View>
                      </View>
                    </View>
                  </View>
                </View>

                <View style={styles.card}>
                  <View style={styles.cardHeader}>
                    <View style={[styles.headerIcon, { backgroundColor: "#fff1d8" }]}>
                      <View style={styles.medIconBottle}>
                        <View style={styles.medIconCap} />
                      </View>
                    </View>
                    <Text style={[styles.cardHeaderText, styles.cardHeaderGrow]}>
                      Active Medications
                    </Text>
                    <View style={styles.activeBadge}>
                      <Text style={styles.activeBadgeText}>{meds.length} active</Text>
                    </View>
                  </View>
                  <View style={styles.cardBody}>
                    <View style={styles.medGrid}>
                      {meds.length ? (
                        meds.map((medicine, index) => (
                          <View key={`${medicine?.medicationName}-${index}`} style={styles.medCell}>
                            <View style={styles.medTitleRow}>
                              <View style={styles.medDot} />
                              <Text style={styles.medName}>
                                {medicine?.medicationName || "Medication"}
                              </Text>
                              <Text style={styles.medSchedule}>
                                {medicine?.frequency || "QAM"}
                              </Text>
                            </View>
                            <Text style={styles.medMeta}>
                              {[medicine?.dosage, medicine?.unit]
                                .filter(Boolean)
                                .join(" ")}
                            </Text>
                            <Text style={styles.medMeta}>
                              {[medicine?.medicationadministered, medicine?.dosageForm]
                                .filter(Boolean)
                                .join(" ")}
                              {medicine?.ingestionMode
                                ? ` · ${medicine.ingestionMode}`
                                : ""}
                            </Text>
                          </View>
                        ))
                      ) : (
                        <Text style={styles.listText}>No active medications</Text>
                      )}
                    </View>
                  </View>
                </View>
              </View>
            </View>
          </View>

          <View style={styles.footer}>
            <Text
              style={styles.footerText}
              render={({ pageNumber, totalPages }) =>
                `Generated by Ogiso EHR · Page ${pageNumber} of ${totalPages}`
              }
            />
            <Text
              style={styles.footerText}
              render={() => ""}
            />
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default ClientSummaryPDF;
